import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bot, Send, Sparkles, User as UserIcon } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SUGGESTED = [
  "What's the difference between ionization and photoelectric detectors?",
  "How do I size batteries for a 24h standby + 5 min alarm system?",
  "Explain Class A vs Class B circuits in one sentence.",
  "Where should I mount a manual pull station?",
];

export const TutorDrawer = ({ lessonTitle }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    try {
      const { data } = await api.post("/tutor/chat", { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "AI tutor is temporarily unavailable";
      toast.error(detail);
      setMessages((m) => [...m, { role: "assistant", content: `⚠️ ${detail}` }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          className="fixed bottom-6 left-6 z-50 bg-red-600 hover:bg-red-700 text-white shadow-lg rounded-sm px-4 py-3 flex items-center gap-2 font-semibold text-sm tracking-wide transition-all hover:-translate-y-0.5"
          data-testid="open-ai-tutor-btn"
        >
          <Sparkles className="w-4 h-4" /> Ask the AI Tutor
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 bg-white flex flex-col" data-testid="ai-tutor-drawer">
        <SheetHeader className="border-b border-zinc-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-red-600 grid place-items-center">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div>
              <SheetTitle className="font-heading text-base tracking-tight">AI Tutor</SheetTitle>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-red-600">
                NFPA 72 • NICET L1 • Claude Sonnet 4.5
              </div>
            </div>
          </div>
          {lessonTitle && (
            <div className="text-xs text-zinc-500 mt-2">Currently studying: <span className="font-semibold text-zinc-800">{lessonTitle}</span></div>
          )}
        </SheetHeader>

        <ScrollArea className="flex-1 px-6 py-4">
          {messages.length === 0 && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-600">
                Ask me anything about fire alarm systems, NFPA 72, installation standards, or your current lesson.
              </p>
              <div className="space-y-2">
                {SUGGESTED.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    disabled={sending}
                    className="block w-full text-left text-sm border border-zinc-200 hover:border-red-300 hover:bg-red-50 px-3 py-2 rounded-sm transition-colors"
                    data-testid={`tutor-suggested-${s.slice(0, 16)}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-4">
            {messages.map((m, i) => (
              <div key={`msg-${i}-${m.role}`} className="flex gap-3 fade-in" data-testid={`tutor-msg-${m.role}-${i}`}>
                <div className={`w-7 h-7 rounded-sm grid place-items-center flex-shrink-0 ${m.role === "user" ? "bg-zinc-950" : "bg-red-600"}`}>
                  {m.role === "user" ? <UserIcon className="w-3.5 h-3.5 text-white" /> : <Bot className="w-3.5 h-3.5 text-white" />}
                </div>
                <div className="text-sm text-zinc-800 leading-relaxed whitespace-pre-wrap">
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex gap-3 text-sm text-zinc-500" data-testid="tutor-typing">
                <div className="w-7 h-7 rounded-sm bg-red-600 grid place-items-center flex-shrink-0">
                  <Bot className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="pt-1">Thinking…</div>
              </div>
            )}
            <div ref={endRef} />
          </div>
        </ScrollArea>

        <div className="border-t border-zinc-200 p-4 flex gap-2 items-center">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            placeholder="Ask about NFPA 72, circuits, spacing…"
            className="rounded-sm"
            disabled={sending}
            data-testid="tutor-input"
          />
          <Button onClick={() => send()} disabled={sending || !input.trim()} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid="tutor-send-btn">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default TutorDrawer;
